import {
  Button,
  ContextMenu,
  Dialog,
  Icon,
  Li,
  Loading,
  Tooltip
} from '@startlibs/components';
import {getColor, isMobile} from '@startlibs/utils'
import {useLazyConstant, usePopupToggle, useToggle} from '@startlibs/core'
import ClipboardJS from 'clipboard'
import React, {useEffect, useRef, useState} from 'react'
import _ from 'lodash/fp'
import styled, {css} from 'styled-components'

import {CopyLinkComponent} from 'sharecomponents/src/pages/ShareDialog'

import {MarkAsReadDialog} from './markAsRead/MarkAsReadDialog'
import {StudyCard} from './StudyCard'
import {TOOLS_LABELS, TOOLS_ICONS} from '../../enums/Tools'
import {getInstantLink, useShareDialog} from './share/useShareDialog'
import {useAsyncDownload} from '../../hooks/useAsyncDownload'
import {useNotification, useNotificationRef} from '../../components/Notifications'
import {SuccessIcon} from '../../components/Styled'
import {FetchErrorDialog} from '../errors/FetchErrorDialog'
import { EditDicomDialog } from './EditDicomDialog';
import { ChangePatientDialog } from './ChangePatientDialog';

const ToolsCellContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 0.25rem;
  > * {
    margin-left: 10px;
    :first-child {
      margin-left: 0;
    }
  }
`
const DropdownButton = styled(Icon)`
  ${Icon} {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%,-50%);
    font-size: 14px;
  }
  color: ${getColor('secondary')};
  cursor: pointer;
  border-radius: 50%;
  display: block;
  background: rgba(41, 122, 163, 0.125);
  width: 2rem;
  height: 2rem;
  position: relative;
  flex-shrink: 0;
  :hover {
    background: rgba(41, 122, 163, 0.175);
  }
  }
`

/*

const userRights = {
  "options": {"useIcons": true, "darkMode": false},
  "userRights": {
    "patient": {"visible": true, "functions": []},
    "study": {"visible": true, "functions": []},
    "physician": {"visible": true, "functions": []},
    "institution": {"visible": true, "functions": []},
    "uploadDate": {"visible": true, "functions": []},
    "assignment": {"visible": true, "functions": {"reassign": true}},
    "status": {"visible": true, "functions": {"changeStatus": true}},
    "tools": {
      "visible": true,
      "functions": {
        "viewStudy": true,
        "viewerLink": true,
        "launchInHoros": true,
        "downloadStudy": false,
        "downloadCD": false,
        "attachNonDicom": false,
        "studyNotes": true,
        "report": true,
        "share": true,
        "markAsRead": true
      }
    }
  }
}
"tools": {
  "viewStudyWeblink": "https://",
    "viewerLinkWeblink": "https://",
    "launchInHorosWeblink": "https://",
    "downloadStudyWeblink": "https://",
    "downloadCDWeblink": "https://",

    "attachNonDicom" : {
    "content" : 3, //backend: count
      "weblink" : "https://"
  },

  "studyNotes" : {
    "content" : true, //backend: true only when there is study notes content
      "weblink" : "https://"
  },

  "report": {
    "right" : "rw", //backend: rw when report by the current user, ro when not report by the current user

      "reportStatus" : "Pending",

      "weblink" : "https://" //no clickable or not visible when right is "ro" and reportStatus is not "Finished"
  },

  "shareStudyWeblink": "https://",

    "markAsReadWeblink": "https://",
}*/

export const ToolButton = styled(({href, badgeCount, badgeStatus, target = "_top", className, ...props}) => <Tooltip
  content={props.label}><a target={target} className={className} href={href}><Icon {...props}/></a></Tooltip>)`
  text-decoration: none;
  color: inherit;
  ${Icon} {
    font-size: 26px;
    color: ${getColor('secondary')};
  }
  ${props => (props.color || props.badgeCount) && css`
    :after {
      content: "${props => props.badgeCount ? props.badgeCount : ''}";
      font-weight: 600;
      color: white;
      font-size: 10px;
      min-width: 15px;
      height: 15px;
      line-height: 11px;
      text-align: center;
      position: absolute;
      bottom: 0;
      right: -5px;
      border-radius: 100px;
      background: ${getColor};
      border: 2px solid white;
      padding: 0 2px;
      white-space: nowrap;
      ${props => props.notReportOwner && css`
        background: transparent;
        box-shadow: inset 0 0 0 2px ${getColor};
      `}
    }
    ${props => props.notReportOwner && css`
      :before {
        content: '';
        width: 15px;
        height: 15px;
        position: absolute;
        bottom: 0;
        right: -5px;
        border-radius: 100px;
        background: white;
      }
    `}
    ${props => props.externalReport && css`
      :after, :before {
        display: none;
      }
    `}
  `}
`

const REPORT_STATUS_TO_COLOR = {
  "Draft": "warning",
  "Reported": "success"
}

const ToolStatus = styled.div`
  width: ${props => props.download ? '23' : props.downloadCD ? '20' : '24'}px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  display: flex;
`
const ToolLoading = styled(Loading)`
  flex-shrink: 0;
  margin-top: -2px;
`
const ErrorIcon = styled(Icon)`
  font-size: 20px;
  color: ${getColor('alert')};
`

const isMarkAsReadDisabled = (status) => status !== "Draft" && status !== "Pending"

const openLinkOnContextMenu = (url, component, notReportOwner) => component === Li ? (() => window.open(url, notReportOwner ? "_blank" : "_top")) : undefined

export const ToolsCell = ({study, refreshList, userRights, setStudy}) => {
  const {tools} = study
  const toolsRights = _.toPairs(userRights.tools.functions)
  const visibleTools = toolsRights.filter(([, isVisible]) => isVisible === 1 && !isMobile())
  const notVisibleTools = toolsRights.filter(([name, isVisible]) => (name !== "reportWriteAccess") && ((isVisible === 0) || (isVisible === 1 && isMobile())))
  const contextMenu = usePopupToggle()
  const [, setNotification] = useNotificationRef()

  const [openDialog, dialog] = useShareDialog(tools.shareStudyWeblink)
  const dicomEdit = useToggle()
  const changePatient = useToggle()
  const [markAsReadDialog, setMarkAsReadDialog] = useState()
  const [instantLinkLoading, setInstantLinkLoading] = useState()
  const [instantShareDialog, setInstantShareDialog] = useState()

  const [startDownload, downloadLoading, downloadStarted] = useAsyncDownload(study, "regular")
  const [startDownloadCD, downloadLoadingCD, downloadStartedCD] = useAsyncDownload(study, "cd")

  const loadInstantLink = () => {
    setInstantShareDialog(true)
    setInstantLinkLoading(true)
    getInstantLink(tools.shareStudyWeblink)
      .then((link) => {
        setInstantShareDialog(link)
      })
      .catch((e) => {
        setInstantShareDialog({error: _.get('0.status', e) || true})
      })
      .finally(() => setInstantLinkLoading(false))
  }

  useEffect(() => {
    if (!instantShareDialog) {
      setInstantLinkLoading(false)
    }
  }, [instantShareDialog])

  const isWriteAccessLegacy = _.isNil(userRights.tools.functions.reportWriteAccess)

  const VISIBLE_TOOLS = ({
    "attachNonDicom": (links, Component = ToolButton) => <Component
      onClick={openLinkOnContextMenu(links.attachNonDicom.weblink, Component)} label={TOOLS_LABELS['attachNonDicom']}
      key="attachNonDicom" color={links.attachNonDicom.content && "success"} badgeCount={links.attachNonDicom.content}
      icon="attachments" href={links.attachNonDicom.weblink}/>,

    "studyNotes": (links, Component = ToolButton) => <Component
      onClick={openLinkOnContextMenu(links.studyNotes.weblink, Component)} disabled={!links.studyNotes.weblink}
      label={TOOLS_LABELS['studyNotes']} color={links.studyNotes.content && "success"} key="studyNotes"
      icon="study-notes" href={links.studyNotes.weblink}/>,

    "report": (links, Component = ToolButton) => {
      const disabled = isWriteAccessLegacy
      ? (links.report.reportStatus !== "Reported" && links.report.right !== "rw") || !links.report.weblink
      : !links.report.weblink

      const notReportOwner = links.report.right !== "rw"
      const externalReport = links.report.right === "dw"
      return <Component onClick={openLinkOnContextMenu(links.report.weblink, disabled ? null : Component,notReportOwner)}
                        notReportOwner={notReportOwner}
                        externalReport={externalReport}
                        target={notReportOwner ? "_blank" : undefined}
                        disabled={disabled}
                        label={
                          externalReport ? "External report" :
                          isWriteAccessLegacy ? "Report" :
                          links.report.reportStatus === "Reported" ? "View report" :
                          links.report.reportStatus === "Draft" ? (notReportOwner ? "Report in progress" : "Edit report") :
                          links.report.right === "rw" ? "Create report" : "No report"                          
                        }
                        color={REPORT_STATUS_TO_COLOR[links.report.reportStatus]} key="report" icon={externalReport ? "external-report" : "report"}
                        href={disabled || ((notReportOwner && Component === Li) && isWriteAccessLegacy) ? undefined : links.report.weblink}/>
    },

    "share": (links, Component = ToolButton) => <Component key="share" label={TOOLS_LABELS['share']} icon="send-line"
                                                           onClick={openDialog}/>,

    "downloadStudy": (links, Component = ToolButton) => downloadLoading ?
      <Tooltip content="Preparing download"><ToolStatus download><ToolLoading size={18}/></ToolStatus></Tooltip>
      : downloadStarted ?
        <Tooltip content="Download started"><ToolStatus download><SuccessIcon icon="check"/></ToolStatus></Tooltip>
        :
        <Component key="downloadStudy" label={TOOLS_LABELS['downloadStudy']} icon={TOOLS_ICONS["downloadStudy"]}
                   href={links["downloadStudyWeblink"]}/>,

    "downloadCD": (links, Component = ToolButton) => downloadLoadingCD ?
      <Tooltip content="Preparing download"><ToolStatus downloadCD><ToolLoading size={18}/></ToolStatus></Tooltip>
      : downloadStartedCD ?
        <Tooltip content="Download started"><ToolStatus downloadCD><SuccessIcon icon="check"/></ToolStatus></Tooltip>
        :
        <Component key="downloadCD" label={TOOLS_LABELS['downloadCD']} icon={TOOLS_ICONS["downloadCD"]}
                   onClick={startDownloadCD(links["downloadCDWeblink"])}/>,

    "markAsRead": (links, Component = ToolButton) => <Component label={TOOLS_LABELS['markAsRead']}
                                                                disabled={isMarkAsReadDisabled(links.report.reportStatus)}
                                                                key="markAsRead" icon="unavailable"
                                                                onClick={() => setMarkAsReadDialog(true)}/>,

    "viewerLink": (links, Component = ToolButton) => <Component label={TOOLS_LABELS["viewerLink"]} key="viewerLink"
                                                                icon={TOOLS_ICONS["viewerLink"]}
                                                                onClick={loadInstantLink}
                                                                isLoading={instantLinkLoading}/>,

    "viewStudy": (links, Component = ToolButton) => <Component
      onClick={Component === ToolButton ? undefined : (() => window.open(links['viewStudyWeblink'], "_blank"))}
      href={Component !== ToolButton ? undefined : links['viewStudyWeblink']} key="viewStudy"
      label={TOOLS_LABELS["viewStudy"]} icon={TOOLS_ICONS["viewStudy"]} target='_blank'/>,

    "dicomEdit": (links, Component = ToolButton) => <Component key="dicomEdit" label={TOOLS_LABELS['dicomEdit']} icon="edit-line"
      onClick={() => {dicomEdit.toggle()}}/>,
    
    "changePatient": (links, Component = ToolButton) => <Component key="changePatient" label={TOOLS_LABELS['changePatient']} icon="change-patient"
      onClick={() => {changePatient.toggle()}}/>,

    "launchInHoros": (links, Component = ToolButton) => <Component
      onClick={openLinkOnContextMenu(links['launchInHorosWeblink'], Component)} key="launchInHoros"
      label={TOOLS_LABELS["launchInHoros"]} icon={TOOLS_ICONS["launchInHoros"]} href={links['launchInHorosWeblink']}/>,

    "activityLogs": (links, Component = ToolButton) => <Component
      onClick={openLinkOnContextMenu(links['activityLogsWeblink'], Component)} key="activityLogs"
      label={TOOLS_LABELS["activityLogs"]} icon={TOOLS_ICONS["activityLogs"]} href={links['activityLogsWeblink']}/>
  })

  const isLoadingContextMenu = instantLinkLoading && !userRights.tools.functions.viewerLink

  const downloadInContextMenu = !visibleTools.find(([tool]) => tool === "downloadStudy")
  const downloadCDInContextMenu = !visibleTools.find(([tool]) => tool === "downloadCD")


  return <ToolsCellContainer>
    {
      visibleTools.map(([tool]) => VISIBLE_TOOLS[tool] && VISIBLE_TOOLS[tool](tools))
    }
    {
      notVisibleTools.length > 0 &&
      (
        ((downloadLoading && downloadInContextMenu) || (downloadLoadingCD && downloadCDInContextMenu))
          ? <Tooltip content="Preparing download"><ToolStatus><ToolLoading size={18}/></ToolStatus></Tooltip>
          : ((downloadStarted && downloadInContextMenu) || (downloadStartedCD && downloadCDInContextMenu))
          ? <Tooltip content="Download started"><ToolStatus><SuccessIcon icon="check"/></ToolStatus></Tooltip>
          : <DropdownButton isLoading={isLoadingContextMenu} onClick={contextMenu.open}><Icon icon="arrow-down"/>
            {
              contextMenu.isOpen &&
              <ContextMenu onCollision={isMobile() ? "fit flip" : undefined}>
                {notVisibleTools.map(([tool]) => VISIBLE_TOOLS[tool] && VISIBLE_TOOLS[tool](tools, Li))}
              </ContextMenu>
            }
          </DropdownButton>
      )
    }
    {
      markAsReadDialog &&
      <MarkAsReadDialog refreshList={refreshList} study={study} closeDialog={() => setMarkAsReadDialog(false)}
                        url={tools.markAsReadWeblink}/>
    }
    {dialog}
    {dicomEdit.isOpen && 
      <EditDicomDialog study={study} setStudy={setStudy} closeDialog={dicomEdit.close} url={tools.editDicomWeblink}/>
    }
    {changePatient.isOpen && 
      <ChangePatientDialog study={study} setStudy={setStudy} closeDialog={changePatient.close} url={tools.editDicomWeblink}/>
    }
    {
      instantShareDialog && !instantShareDialog.error &&
      <Dialog
        className="JS-copy-focus-container"
        title="Study images webviewer link"
        closeDialog={() => setInstantShareDialog(false)}
        footer={<>
          <Button onClick={() => setInstantShareDialog(false)}>Cancel</Button>
          <CopyButton highlight text={instantShareDialog} close={(msg) => {
            setNotification(msg);
            setInstantShareDialog(false)
          }} isLoading={instantLinkLoading}>Copy link & close</CopyButton>
        </>}
      >
        <StudyCard study={study}/>
        <CopyLinkComponent url={instantShareDialog} label="Use the link below to view study images in browser:"
                           key={instantShareDialog} loadingShareableLink={instantLinkLoading}/>
      </Dialog>
    }
    {
      instantShareDialog && instantShareDialog.error &&
      <FetchErrorDialog closeDialog={() => setInstantShareDialog(false)}
                        message="An error has occurred while trying to load the link. Please refresh the page and try again."/>

    }
  </ToolsCellContainer>
}

const CopyButton = ({text, close, isLoading}) => {
  const ref = useRef()
  useEffect(() => {
    const btn = new ClipboardJS('.copy-to-clipboard', {
      container: ref.current.closest('.JS-copy-focus-container')
    })
    return () => btn.destroy()
  }, [])

  return <Button
    buttonRef={ref}
    className="copy-to-clipboard"
    isLoading={isLoading}
    data-clipboard-text={text}
    highlight
    onClick={() => close(ClipboardJS.isSupported() ? 'Shareable link copied to clipboard' : "You've finished the share function")}
  >
    {ClipboardJS.isSupported() ? 'Copy link & Close' : 'Done'}
  </Button>
}
